import React, { useState, useEffect, useRef, memo, useMemo, useLayoutEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Box,
  Typography,
  Autocomplete,
  Chip,
  Paper,
} from '@mui/material';
import { Unit, POI } from '../types/index.ts';
import { useToast } from '../contexts/ToastContext.tsx';
import { useConfig } from '../contexts/ConfigContext.tsx';
import { http } from '../lib/http';

interface CreateCallModalProps {
  open: boolean;
  onClose: () => void;
  onCallCreated: (call: any) => void;
}

const CreateCallModal: React.FC<CreateCallModalProps> = ({
  open,
  onClose,
  onCallCreated,
}) => {
  const { showSuccess, showError } = useToast();
  const { callCodes, pois } = useConfig();
  const [formData, setFormData] = useState({
    status: 'PENDING',
    callCode: '',
    location: '',
    boxArea: '',
    tacChannel: '',
    timeReceived: '',
    reportedVia: '',
    reportingParty: '',
    priority: 3,
    units: [] as string[],
    notes: '',
  });
  
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [showLocationConfirm, setShowLocationConfirm] = useState(false);
  const [pendingLocation, setPendingLocation] = useState('');
  const [poiSelectedFromDropdown, setPoiSelectedFromDropdown] = useState(false);
  const [poiInput, setPoiInput] = useState('');
  const [poiOpen, setPoiOpen] = useState(false);

  // Cursor position tracking refs (similar to PCRModal)
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement | null }>({});
  const cursorPositionsRef = useRef<{ [key: string]: number }>({});
  const pendingCursorRestoration = useRef<{ [key: string]: number }>({});
  const composingRef = useRef(false);

  // Utility function to check for exact POI match
  const isExactPoiMatch = (name: string) =>
    pois.some(p => p.name.toLowerCase() === name.trim().toLowerCase());

  // Store cursor positions before any state updates
  const storeCursorPosition = useCallback((field: string) => {
    const input = inputRefs.current[field];
    if (input && document.activeElement === input) {
      const position = (input as HTMLInputElement | HTMLTextAreaElement).selectionStart || 0;
      cursorPositionsRef.current[field] = position;
    }
  }, []);

  // Handle cursor position tracking events
  const handleCursorPositionChange = useCallback((field: string) => {
    return () => {
      const input = inputRefs.current[field];
      if (input && document.activeElement === input) {
        const position = (input as HTMLInputElement | HTMLTextAreaElement).selectionStart || 0;
        cursorPositionsRef.current[field] = position;
      }
    };
  }, []);

  // Restore cursor positions after DOM updates (optimized)
  useLayoutEffect(() => {
    const keys = Object.keys(pendingCursorRestoration.current);
    if (!keys.length) return; // Skip RAF if no restorations needed
    
    keys.forEach(field => {
      const position = pendingCursorRestoration.current[field];
      const input = inputRefs.current[field];
      
      if (input && document.activeElement === input && position !== undefined) {
        requestAnimationFrame(() => {
          // Extra guard: check if element is still active and available
          const el = inputRefs.current[field];
          if (!el || document.activeElement !== el) return;
          
          try {
            // Clamp position to input value length to handle cases where value is shorter
            const clampedPosition = Math.min(position, (el as HTMLInputElement | HTMLTextAreaElement).value.length);
            (el as HTMLInputElement | HTMLTextAreaElement).setSelectionRange(clampedPosition, clampedPosition);
          } catch (error) {
            // Silently handle cursor restoration failures
          }
        });
      }
    });
    
    // Clear pending restorations
    pendingCursorRestoration.current = {};
  });

  // Memoize call code options to prevent scroll reset
  const callCodeOptions = useMemo(() => {
    if (!callCodes) return [];
    
    return Object.entries(callCodes).map(([code, data]) => ({
      code,
      desc: data.desc,
      box: data.box,
      pri: data.pri || 3, // Default to priority 3 if not set
      label: `${code} — ${data.desc}`,
    }));
  }, [callCodes]);

  // Memoize listbox style to avoid identity churn
  const listboxStyle = useMemo(
    () => ({ maxHeight: '300px', overflow: 'auto' } as React.CSSProperties),
    []
  );

  // Memoize Paper component to avoid identity churn
  const AutocompletePaper = useMemo(
    () =>
      function AutocompletePaper({ children, ...other }: any) {
        return (
          <Paper {...other} sx={{ backgroundColor: '#1a1d22', border: '1px solid #464a52' }}>
            {children}
          </Paper>
        );
      },
    []
  );

  // Load units when modal opens
  useEffect(() => {
    if (open) {
      loadUnits();
      resetForm();
    }
  }, [open]);

  const loadUnits = async () => {
    try {
      const unitsResponse = await http.get('/units');
      if (unitsResponse.status === 200 && unitsResponse.data.success) {
        setUnits(unitsResponse.data.data.filter((unit: Unit) => unit.status !== 'OOS'));
      }
    } catch (error) {
      console.error('Failed to load units:', error);
    }
  };

  const resetForm = () => {
    const now = new Date();
    const timeString = now.toUTCString().replace('GMT', 'UTC');
    
    setFormData({
      status: 'PENDING',
      callCode: '',
      location: '',
      boxArea: '',
      tacChannel: '',
      timeReceived: timeString,
      reportedVia: '',
      reportingParty: '',
      priority: 3,
      units: [],
      notes: '',
    });
    setErrors({});
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form on submit
    const newErrors: Record<string, boolean> = {};
    let valid = true;

    // Call Code (required)
    if (!formData.callCode || formData.callCode === '') {
      newErrors.callCode = true;
      valid = false;
    }

    // Box Area (required)
    if (!formData.boxArea || formData.boxArea === '') {
      newErrors.boxArea = true;
      valid = false;
    }

    // Location (required)
    if (!formData.location || formData.location === '') {
      newErrors.location = true;
      valid = false;
    }

    setErrors(newErrors);
    
    if (!valid) return;

    try {
      setLoading(true);
      
      // Prepare payload matching the Lua version
      const payload = {
        status: formData.status,
        callCode: formData.callCode,
        location: formData.location.toUpperCase(),
        boxArea: formData.boxArea,
        tacChannel: formData.tacChannel || 'PRMY',
        reportedVia: formData.reportedVia || 'Radio Report',
        reportingParty: formData.reportingParty.toUpperCase(),
        priority: formData.priority,
        notes: formData.notes.toUpperCase(),
        assignedUnits: formData.units,
        createdBy: 'Dispatcher',
      };

      const response = await http.post('/calls', payload);

      const data = response.data;
      if (data.success) {
        showSuccess(`Call ${data.data.id} created successfully`);
        onCallCreated(data.data);
        handleClose();
      } else {
        showError('Failed to create call');
      }
    } catch (error) {
      showError('Failed to create call');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleCallCodeChange = (callCode: string) => {
    const codeData = callCodes[callCode];
    setFormData(prev => ({
      ...prev,
      callCode,
      priority: codeData ? (codeData.pri || 3) : 3,
    }));
    // Clear error when user selects a call code
    setErrors(prev => ({ ...prev, callCode: false }));
  };

  const handlePOISelect = (poi: POI | null) => {
    if (poi) {
      setPoiSelectedFromDropdown(true); // Mark that POI was selected from dropdown
      setFormData(prev => ({
        ...prev,
        location: poi.name,
        boxArea: poi.boxLocation,
      }));
    }
  };

  const handleLocationInputChange = (value: string) => {
    // Store cursor position before state update
    storeCursorPosition('location');
    
    // Mark this field for cursor restoration
    const currentPosition = cursorPositionsRef.current['location'];
    if (currentPosition !== undefined) {
      pendingCursorRestoration.current['location'] = currentPosition;
    }
    
    // Just update the form data, don't trigger confirmation yet
    setFormData(prev => ({ ...prev, location: value.toUpperCase() }));
    // Clear error when user types in location
    setErrors(prev => ({ ...prev, location: false }));
    // Reset flag when user types (not selecting from dropdown)
    setPoiSelectedFromDropdown(false);
  };

  const handleLocationKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      // If popup is open, let Autocomplete handle the selection
      if (poiOpen) return;

      const value = (event.target as HTMLInputElement).value.trim();
      if (value !== '' && !poiSelectedFromDropdown && !isExactPoiMatch(value)) {
        setPendingLocation(value.toUpperCase());
        setShowLocationConfirm(true);
      }
    }
  };

  const handleLocationConfirm = (confirmed: boolean) => {
    if (confirmed) {
      setFormData(prev => ({ ...prev, location: pendingLocation }));
      setPoiInput(pendingLocation);
    } else {
      setFormData(prev => ({ ...prev, location: '' }));
      setPoiInput('');
      setTimeout(() => (inputRefs.current['location'] as HTMLInputElement)?.focus(), 100);
    }
    setShowLocationConfirm(false);
    setPendingLocation('');
  };



  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '900px',
          maxHeight: '90vh',
          backgroundColor: '#1c1f25', // surface
          border: '1px solid #40444b', // border
          borderRadius: '12px',
          userSelect: 'text', // Re-enable text selection in modal
        },
      }}
    >
      <DialogTitle sx={{ 
        color: '#eceef1', 
        fontSize: '22px',
        fontWeight: 'bold',
        borderBottom: 'none',
        padding: '12px 24px 0 24px'
      }}>
        New Call
      </DialogTitle>
      
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <DialogContent sx={{ 
          padding: '6px 24px',
          flex: 1,
          overflow: 'auto'
        }}>
          <Grid container spacing={1} sx={{ height: '100%' }}>
            {/* LEFT COLUMN */}
            <Grid item xs={6} sx={{ paddingRight: '8px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
                {/* STATUS */}
                <Box>
                  <Typography sx={{ color: '#b4b9c0', fontSize: '14px', marginBottom: '4px' }}>
                    STATUS
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.status || ''}
                    InputProps={{ readOnly: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '28px',
                        backgroundColor: '#1a1d22',
                        '& fieldset': { borderColor: '#464a52' },
                      },
                      '& .MuiInputBase-input': { color: '#eceef1', fontSize: '14px' }
                    }}
                  />
                </Box>

                {/* CALL CODE */}
                <Box>
                  <Typography sx={{ color: '#b4b9c0', fontSize: '14px', marginBottom: '4px' }}>
                    CALL CODE
                  </Typography>
                  <Autocomplete
                    options={callCodeOptions}
                    value={callCodeOptions.find(opt => opt.code === formData.callCode) || null}
                    onChange={(_, value) => handleCallCodeChange(value?.code || '')}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue || inputValue.trim() === '') {
                        return options; // Show all options when search is empty
                      }
                      
                      const searchValue = inputValue.toLowerCase().trim();
                      const filtered = options.filter(option => {
                        return (
                          option.code.toLowerCase().includes(searchValue) ||
                          option.desc.toLowerCase().includes(searchValue) ||
                          option.box.toLowerCase().includes(searchValue) ||
                          option.label.toLowerCase().includes(searchValue)
                        );
                      });
                      return filtered;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        error={!!errors.callCode}
                        placeholder="Search and select a call code..."
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '30px',
                            backgroundColor: '#1a1d22',
                          },
                        
                          /* base / hover / focus */
                          '&& .MuiOutlinedInput-notchedOutline': {
                            borderColor: errors.callCode ? '#dc5050 !important' : '#464a52',
                            borderWidth: errors.callCode ? '2px' : '1px',
                            borderStyle: 'solid',
                          },
                          '&&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: errors.callCode ? '#dc5050 !important' : '#8c9198',
                          },
                          '&& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: errors.callCode ? '#dc5050 !important' : '#8c9198',
                          },
                        
                          '& .MuiInputBase-input': { color: '#eceef1', fontSize: '14px' },
                        }}
                        
                      />
                    )}
                    
                    
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      return (
                        <Box component="li" key={key} {...otherProps}>
                        <Box>
                          <Typography sx={{ color: '#eceef1', fontSize: '14px' }}>
                            {option.code} — {option.desc}
                          </Typography>
                          <Typography sx={{ color: '#8c9198', fontSize: '12px' }}>
                            Box: {option.box || 'N/A'} • Priority: {option.pri || 3}
                          </Typography>
                        </Box>
                      </Box>
                      );
                    }}
                    PaperComponent={AutocompletePaper}
                    ListboxProps={{ style: listboxStyle }}
                    noOptionsText="No call codes found"
                  />
                </Box>

                {/* LOCATION / ADDRESS */}
                <Box>
                  <Typography sx={{ color: '#b4b9c0', fontSize: '14px', marginBottom: '4px' }}>
                    LOCATION / ADDRESS
                  </Typography>
                  <Autocomplete
                    freeSolo
                    options={pois || []}
                    // keep a controlled input value so we can check it on close
                    inputValue={poiInput}
                    onInputChange={(_, value, reason) => {
                      // Update input, uppercase in form state for display if you want
                      setPoiInput(value);
                      handleLocationInputChange(typeof value === 'string' ? value : '');
                      if (reason === 'clear') setPoiSelectedFromDropdown(false);
                    }}

                    // IMPORTANT: equality for POI objects
                    isOptionEqualToValue={(opt, val) => opt.name === val.name}

                    // when a POI is chosen from the list
                    onChange={(_, value, reason) => {
                      if (reason === 'selectOption' && value && typeof value !== 'string') {
                        setPoiSelectedFromDropdown(true);
                        handlePOISelect(value as POI);
                        // sync input text to the selected option
                        setPoiInput(value.name);
                      }
                    }}

                    // control popup open/close so blur doesn't fire your dialog mid-hover
                    open={poiOpen}
                    onOpen={() => setPoiOpen(true)}
                    onClose={() => {
                      setPoiOpen(false);
                      // Only confirm when the list closes WITHOUT selecting an option
                      // and there is non-empty text that isn't an exact POI name.
                      if (!poiSelectedFromDropdown && poiInput.trim() !== '' && !isExactPoiMatch(poiInput)) {
                        setPendingLocation(poiInput.toUpperCase());
                        setShowLocationConfirm(true);
                      }
                      // reset the flag for the next interaction
                      setPoiSelectedFromDropdown(false);
                    }}

                    // keep the selected value in sync with formData
                    value={pois.find(p => p.name === formData.location) || null}
                    getOptionLabel={(option) => (typeof option === 'string'
                      ? option
                      : option.code ? `${option.code} — ${option.name}` : option.name)}

                    filterOptions={(options, { inputValue }) => {
                      const s = inputValue.toLowerCase();
                      return options.filter(o =>
                        o.name.toLowerCase().includes(s) ||
                        o.address.toLowerCase().includes(s) ||
                        (o.code && o.code.toLowerCase().includes(s)));
                    }}

                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputRef={(el) => { 
                          inputRefs.current['location'] = el;
                        }}
                        variant="outlined"
                        error={!!errors.location}
                        placeholder="Type to search POIs or enter custom location..."
                        onKeyPress={handleLocationKeyPress}
                        inputProps={{
                          ...params.inputProps,
                          onCompositionStart: () => { composingRef.current = true; },
                          onCompositionEnd: () => { composingRef.current = false; },
                          onFocus: handleCursorPositionChange('location'),
                          onSelect: handleCursorPositionChange('location'),
                          onClick: handleCursorPositionChange('location'),
                          onKeyUp: handleCursorPositionChange('location'),
                          spellCheck: false,
                          autoComplete: 'off'
                        }}
                        // ❌ remove onBlur here – it causes the popup to remount while hovering
                        sx={{
                          '& .MuiOutlinedInput-root': { height: '30px', backgroundColor: '#1a1d22' },
                          '&& .MuiOutlinedInput-notchedOutline': {
                            borderColor: errors.location ? '#dc5050 !important' : '#464a52',
                            borderWidth: errors.location ? '2px' : '1px',
                            borderStyle: 'solid',
                          },
                          '& .MuiInputBase-input': {
                            color: '#eceef1',
                            fontSize: '14px',
                            textTransform: 'uppercase',
                          },
                        }}
                      />
                    )}

                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      return (
                        <Box component="li" key={key} {...otherProps}>
                          <Box>
                            <Typography sx={{ color: '#eceef1', fontSize: '14px' }}>
                              {option.code ? `${option.code} — ${option.name}` : option.name}
                            </Typography>
                            <Typography sx={{ color: '#8c9198', fontSize: '12px' }}>
                              {option.address} • {option.boxLocation}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }}

                    PaperComponent={({ children, ...other }) => (
                      <Paper {...other} sx={{ backgroundColor: '#1a1d22', border: '1px solid #464a52' }}>
                        {children}
                      </Paper>
                    )}
                    ListboxProps={{ style: { maxHeight: '300px', overflow: 'auto' } }}
                  />
                </Box>

                {/* BOX AREA */}
                <Box>
                  <Typography sx={{ color: '#b4b9c0', fontSize: '14px', marginBottom: '4px' }}>
                    BOX AREA
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={formData.boxArea || ''}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, boxArea: e.target.value }));
                        // Clear error when user selects a box area
                        setErrors(prev => ({ ...prev, boxArea: false }));
                      }}
                      displayEmpty
                      sx={{
                        height: '30px',
                        backgroundColor: '#1a1d22',
                        '& .MuiOutlinedInput-notchedOutline': { 
                          borderColor: errors.boxArea ? '#dc5050' : '#464a52',
                          borderWidth: errors.boxArea ? '2px' : '1px'
                        },
                        '& .MuiSelect-select': { color: '#eceef1', fontSize: '14px' }
                      }}
                    >
                      <MenuItem value="">
                        <em>Select Box Area</em>
                      </MenuItem>
                      <MenuItem value="REDWOOD">REDWOOD</MenuItem>
                      <MenuItem value="PROMINENCE">PROMINENCE</MenuItem>
                      <MenuItem value="ARBORFIELD">ARBORFIELD</MenuItem>
                      <MenuItem value="HILLVIEW">HILLVIEW</MenuItem>
                      <MenuItem value="GREENDALE">GREENDALE</MenuItem>
                      <MenuItem value="UNINC STAPLETON">UNINC STAPLETON</MenuItem>
                      <MenuItem value="UNKNOWN">UNKNOWN</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* TAC CHANNEL */}
                <Box>
                  <Typography sx={{ color: '#b4b9c0', fontSize: '14px', marginBottom: '4px' }}>
                    TAC CHANNEL
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={formData.tacChannel || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, tacChannel: e.target.value }))}
                      displayEmpty
                      sx={{
                        height: '30px',
                        backgroundColor: '#1a1d22',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#464a52' },
                        '& .MuiSelect-select': { color: '#eceef1', fontSize: '14px' }
                      }}
                    >
                      <MenuItem value="">
                        <em>Select TAC Channel</em>
                      </MenuItem>
                      <MenuItem value="PRMY">PRMY</MenuItem>
                      <MenuItem value="TAC1">TAC1</MenuItem>
                      <MenuItem value="TAC2">TAC2</MenuItem>
                      <MenuItem value="EVNT">EVNT</MenuItem>
                      <MenuItem value="FIRE1">FIRE1</MenuItem>
                      <MenuItem value="FIRE2">FIRE2</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Grid>

            {/* RIGHT COLUMN */}
            <Grid item xs={6} sx={{ paddingLeft: '8px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
                {/* TIME RECEIVED (UTC) */}
                <Box>
                  <Typography sx={{ color: '#b4b9c0', fontSize: '14px', marginBottom: '4px' }}>
                    TIME RECEIVED (UTC)
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.timeReceived || ''}
                    InputProps={{ readOnly: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '28px',
                        backgroundColor: '#1a1d22',
                        '& fieldset': { borderColor: '#464a52' },
                      },
                      '& .MuiInputBase-input': { color: '#eceef1', fontSize: '14px' }
                    }}
                  />
                </Box>

                {/* REPORTED VIA */}
                <Box>
                  <Typography sx={{ color: '#b4b9c0', fontSize: '14px', marginBottom: '4px' }}>
                    REPORTED VIA
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={formData.reportedVia || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, reportedVia: e.target.value }))}
                      displayEmpty
                      sx={{
                        height: '30px',
                        backgroundColor: '#1a1d22',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#464a52' },
                        '& .MuiSelect-select': { color: '#eceef1', fontSize: '14px' }
                      }}
                    >
                      <MenuItem value="">
                        <em>Select Reported Via</em>
                      </MenuItem>
                      <MenuItem value="Radio Report">Radio Report</MenuItem>
                      <MenuItem value="911 Caller">911 Caller</MenuItem>
                      <MenuItem value="SC Dispatch">SC Dispatch</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* REPORTING PARTY */}
                <Box>
                  <Typography sx={{ color: '#b4b9c0', fontSize: '14px', marginBottom: '4px' }}>
                    REPORTING PARTY
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.reportingParty || ''}
                    onChange={(e) => {
                      // Store cursor position before state update
                      storeCursorPosition('reportingParty');
                      const currentPosition = cursorPositionsRef.current['reportingParty'];
                      if (currentPosition !== undefined) {
                        pendingCursorRestoration.current['reportingParty'] = currentPosition;
                      }
                      setFormData(prev => ({ ...prev, reportingParty: e.target.value.toUpperCase() }));
                    }}
                    inputRef={(el) => { inputRefs.current['reportingParty'] = el; }}
                    inputProps={{
                      onCompositionStart: () => { composingRef.current = true; },
                      onCompositionEnd: () => { composingRef.current = false; },
                      onFocus: handleCursorPositionChange('reportingParty'),
                      onSelect: handleCursorPositionChange('reportingParty'),
                      onClick: handleCursorPositionChange('reportingParty'),
                      onKeyUp: handleCursorPositionChange('reportingParty'),
                      spellCheck: false,
                      autoComplete: 'off'
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '28px',
                        backgroundColor: '#1a1d22',
                        '& fieldset': { borderColor: '#464a52' },
                      },
                      '& .MuiInputBase-input': { 
                        color: '#eceef1', 
                        fontSize: '14px',
                        textTransform: 'uppercase'
                      }
                    }}
                  />
                </Box>

                {/* PRIORITY (1-3) */}
                <Box>
                  <Typography sx={{ color: '#b4b9c0', fontSize: '14px', marginBottom: '4px' }}>
                    PRIORITY (1-3)
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.priority || ''}
                    InputProps={{ readOnly: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '28px',
                        backgroundColor: '#1a1d22',
                        '& fieldset': { borderColor: '#464a52' },
                      },
                      '& .MuiInputBase-input': { color: '#eceef1', fontSize: '14px' }
                    }}
                  />
                </Box>

                {/* UNITS (Select Multiple) */}
                <Box>
                  <Typography sx={{ color: '#b4b9c0', fontSize: '14px', marginBottom: '4px' }}>
                    UNITS (Select Multiple)
                  </Typography>
                  <Autocomplete
                    multiple
                    options={units ? units.map(unit => unit.id) : []}
                    value={formData.units}
                    onChange={(_, value) => setFormData(prev => ({ ...prev, units: value }))}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option}
                          label={option}
                          size="small"
                          sx={{
                            backgroundColor: '#2a2a2a',
                            color: '#eceef1',
                            '& .MuiChip-deleteIcon': { color: '#b4b9c0' }
                          }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Units"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            minHeight: '30px',
                            backgroundColor: '#1a1d22',
                            '& fieldset': { borderColor: '#464a52' },
                          },
                          '& .MuiInputBase-input': { color: '#eceef1', fontSize: '14px' }
                        }}
                      />
                    )}
                    PaperComponent={({ children, ...other }) => (
                      <Paper {...other} sx={{ 
                        backgroundColor: '#1a1d22', 
                        border: '1px solid #464a52'
                      }}>
                        {children}
                      </Paper>
                    )}
                    ListboxProps={{
                      style: {
                        maxHeight: '300px',
                        overflow: 'auto'
                      }
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        {/* FOOTER with notes and buttons */}
        <Box sx={{ 
          padding: '6px 24px',
          borderTop: '1px solid #464a52',
          backgroundColor: '#1a1d22',
          border: '1px solid #464a52',
          margin: '0 12px 12px 12px',
          borderRadius: '4px',
          flexShrink: 0
        }}>
          {/* CALL NOTES */}
          <Box sx={{ marginBottom: '8px' }}>
            <Typography sx={{ color: '#b4b9c0', fontSize: '14px', marginBottom: '4px' }}>
              CALL NOTES
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={formData.notes || ''}
              onChange={(e) => {
                // Store cursor position before state update
                storeCursorPosition('notes');
                const currentPosition = cursorPositionsRef.current['notes'];
                if (currentPosition !== undefined) {
                  pendingCursorRestoration.current['notes'] = currentPosition;
                }
                setFormData(prev => ({ ...prev, notes: e.target.value.toUpperCase() }));
              }}
              inputRef={(el) => { inputRefs.current['notes'] = el; }}
              inputProps={{
                onCompositionStart: () => { composingRef.current = true; },
                onCompositionEnd: () => { composingRef.current = false; },
                onFocus: handleCursorPositionChange('notes'),
                onSelect: handleCursorPositionChange('notes'),
                onClick: handleCursorPositionChange('notes'),
                onKeyUp: handleCursorPositionChange('notes'),
                spellCheck: false,
                autoComplete: 'off'
              }}
              placeholder="Additional information about the call..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#1a1d22',
                  '& fieldset': { borderColor: '#464a52' },
                },
                '& .MuiInputBase-input': { 
                  color: '#eceef1', 
                  fontSize: '14px',
                  textTransform: 'uppercase'
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#b4b9c0',
                  opacity: 1
                }
              }}
            />
          </Box>

          {/* Buttons */}
          <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button
              onClick={handleClose}
              sx={{
                width: '120px',
                height: '40px',
                backgroundColor: '#2a2a2a',
                color: '#eceef1',
                border: '1px solid #464a52',
                borderRadius: '5px',
                '&:hover': { backgroundColor: '#3a3a3a' }
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              sx={{
                width: '140px',
                height: '40px',
                backgroundColor: '#b4d4b4',
                color: '#282828',
                border: '2px solid #464a52',
                borderRadius: '5px',
                fontWeight: 'bold',
                '&:hover': { 
                  backgroundColor: '#a4c4a4'
                },
                '&:disabled': {
                  backgroundColor: '#8c8c8c',
                  color: '#646464'
                }
              }}
            >
              {loading ? 'Creating...' : 'Create Call'}
            </Button>
          </Box>
        </Box>
      </form>

      {/* Location Confirmation Dialog */}
      <Dialog
        open={showLocationConfirm}
        onClose={() => handleLocationConfirm(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#1c1f25',
            border: '1px solid #40444b',
            borderRadius: '12px',
            minWidth: '400px',
          },
        }}
      >
        <DialogTitle sx={{ color: '#eceef1', fontSize: '18px', fontWeight: 'bold' }}>
          Use Custom Location
        </DialogTitle>
        <DialogContent sx={{ padding: '16px 24px' }}>
          <Typography sx={{ color: '#b4b9c0', fontSize: '14px' }}>
            Use '{pendingLocation}' as the call location?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: '8px' }}>
          <Button
            onClick={() => handleLocationConfirm(false)}
            sx={{
              backgroundColor: '#2a2a2a',
              color: '#eceef1',
              border: '1px solid #464a52',
              borderRadius: '5px',
              '&:hover': { backgroundColor: '#3a3a3a' }
            }}
          >
            No
          </Button>
          <Button
            onClick={() => handleLocationConfirm(true)}
            sx={{
              backgroundColor: '#b4d4b4',
              color: '#282828',
              border: '2px solid #464a52',
              borderRadius: '5px',
              fontWeight: 'bold',
              '&:hover': { backgroundColor: '#a4c4a4' }
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default memo(CreateCallModal);